import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { ArrowLeft, Phone, Mail, MapPin, MessageCircle, Clock, CreditCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  preparing: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  ready: 'bg-green-500/20 text-green-400 border-green-500/50',
  delivered: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/50',
};

const statusSteps = ['pending', 'preparing', 'ready', 'delivered'];

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, { headers });
      setOrder(res.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Order not found');
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, { headers });
      setOrder({ ...order, status: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Order Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!order) return null;

  const currentStepIndex = statusSteps.indexOf(order.status);

  return (
    <AdminLayout title={`Order #${order.order_number}`}>
      <div data-testid="admin-order-detail">
        {/* Back Button */}
        <Link to="/admin/orders" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          Back to Orders
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Header */}
            <div className="bg-card rounded-xl border border-white/10 p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-heading text-white">Order #{order.order_number}</h2>
                  <p className="text-gray-400 flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4" />
                    {formatDateTime(order.created_at)}
                  </p>
                </div>
                <Badge className={`${statusColors[order.status]} border capitalize text-lg px-4 py-2`}>
                  {order.status}
                </Badge>
              </div>

              {/* Status Timeline */}
              <div className="flex items-center justify-between mb-6">
                {statusSteps.map((step, index) => {
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  return (
                    <div key={step} className="flex items-center">
                      <div className={`flex flex-col items-center ${index > 0 ? 'flex-1' : ''}`}>
                        {index > 0 && (
                          <div className={`h-1 w-full ${isCompleted ? 'bg-fire-500' : 'bg-white/10'}`} />
                        )}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-fire-500 text-white' : 'bg-white/10 text-gray-500'
                        }`}>
                          {index + 1}
                        </div>
                        <span className={`text-xs mt-1 capitalize ${isCurrent ? 'text-fire-500' : 'text-gray-500'}`}>
                          {step}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Update Status */}
              <div className="flex items-center gap-4">
                <span className="text-gray-400">Update Status:</span>
                <Select value={order.status} onValueChange={updateStatus} disabled={updating}>
                  <SelectTrigger className="w-40 bg-black/50 border-white/10 text-white" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {statusSteps.map(status => (
                      <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                    ))}
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-card rounded-xl border border-white/10 p-6">
              <h3 className="text-white font-bold text-lg mb-4">Order Items</h3>
              
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/50">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.name}</p>
                      <p className="text-gray-400 text-sm">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">₦{(item.price * item.quantity).toLocaleString()}</p>
                      <p className="text-gray-400 text-sm">₦{item.price.toLocaleString()} each</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 mt-6 pt-4 space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span>
                  <span className="text-white">₦{order.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Delivery Fee</span>
                  <span className="text-white">₦{order.delivery_fee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10">
                  <span className="text-white">Total</span>
                  <span className="text-fire-500">₦{order.total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="bg-card rounded-xl border border-white/10 p-6">
              <h3 className="text-white font-bold text-lg mb-4">Customer Information</h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-fire-500/20 flex items-center justify-center">
                    <span className="text-fire-500">👤</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{order.customer_name}</p>
                    <p className="text-gray-400 text-sm">Customer</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-gray-400">
                  <Phone className="w-5 h-5" />
                  <span className="text-white">{order.customer_phone}</span>
                </div>
                
                {order.customer_email && (
                  <div className="flex items-center gap-3 text-gray-400">
                    <Mail className="w-5 h-5" />
                    <span className="text-white">{order.customer_email}</span>
                  </div>
                )}
                
                {order.customer_address && (
                  <div className="flex items-start gap-3 text-gray-400">
                    <MapPin className="w-5 h-5 flex-shrink-0" />
                    <span className="text-white">{order.customer_address}</span>
                  </div>
                )}

                {order.special_instructions && (
                  <div className="mt-4 p-3 bg-black/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Special Instructions:</p>
                    <p className="text-white">{order.special_instructions}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-card rounded-xl border border-white/10 p-6">
              <h3 className="text-white font-bold text-lg mb-4">Payment Information</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Method</span>
                  <span className="text-white capitalize flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    {order.payment_method}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment Status</span>
                  <Badge className={order.payment_status === 'paid' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/50' 
                    : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                  }>
                    {order.payment_status}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Actions */}
            <a
              href={`https://wa.me/${order.customer_phone.replace(/[^0-9]/g, '')}?text=Hi ${order.customer_name}! Regarding your order #${order.order_number}...`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white" data-testid="contact-whatsapp">
                <MessageCircle className="w-5 h-5 mr-2" />
                Contact Customer on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
